// resources/js/Pages/HallOfFame/components/ScriptRowExpanded.jsx
import React from 'react';
import EmotionChart from './EmotionChart';
import RadarChart from './RadarChart';

export default function ScriptRowExpanded({ script, allScripts, onContactWriterClick, auth, detailLoading = false }) {
    // USP resolution priority:
    //   1. pcrh_usp  — structured 4-field object from the pcrh loglines+USP call
    //   2. usp_legacy — free-form string from the legacy movie.usp column
    //   3. pcr_list[firstKey].USP — historical fallback baked into pcr_list
    const pcrhUsp = script.pcrh_usp || null;
    const hasStructuredUsp = !!(pcrhUsp && (
        pcrhUsp.core_hook ||
        pcrhUsp.distinctive_experience ||
        pcrhUsp.audience_lane ||
        pcrhUsp.execution_dependency
    ));
    const legacyUsp = script.usp_legacy
        || (script.pcr_list ? Object.values(script.pcr_list)[0]?.USP : null)
        || null;
    
    // Check if current user is the author by comparing nicknames (more secure than IDs)
    const currentUserNickname = auth?.user?.nickname || null;
    const scriptAuthorNickname = script.author?.nickname || null;
    
    // Debug logging - remove after testing
    console.log('[ScriptRowExpanded Debug]', {
        scriptTitle: script.title,
        currentUserNickname,
        scriptAuthorNickname,
        authObject: auth,
        scriptAuthor: script.author
    });
    
    const isOwnScript = currentUserNickname && scriptAuthorNickname && 
                       currentUserNickname === scriptAuthorNickname;
    
    // Extract emotion summaries from script_emotional_use_analysis
    const emotionSummaries = {};
    if (script.script_emotional_use_analysis) {
        try {
            const analysis = typeof script.script_emotional_use_analysis === 'string' 
                ? JSON.parse(script.script_emotional_use_analysis) 
                : script.script_emotional_use_analysis;
            
            if (analysis?.emotions) {
                analysis.emotions.forEach(item => {
                    emotionSummaries[item.emotion] = item.executiveSummary;
                });
            }
        } catch (e) {
            console.error('Error parsing emotion analysis:', e);
        }
    }
    
    return (
        <tr className="hiddenRow">
            <td colSpan="27">
                <div className="screenplay_info">
                    <div className="row align-items-center gx-2">
                        {/* Column 1: Logline for vault, Poster for non-vault */}
                        <div className="col-5 col-lg-3 mb-3 mb-lg-0">
                            {script.isVault ? (
                                /* Vault: show logline instead of poster */
                                <div className="screenplay_content">
                                    <fieldset>
                                        <legend>Logline</legend>
                                        <p>{script.logline || 'No logline available'}</p>
                                    </fieldset>
                                </div>
                            ) : (
                                /* Non-vault: show poster */
                                <>
                                    {script.poster_url && (
                                        <figure className="mb-0 position-relative">
                                            <img
                                                src={script.poster_url}
                                                alt={script.title}
                                                loading="lazy"
                                                width="250"
                                                height="184"
                                                className="w-100 img-fluid"
                                            />
                                        </figure>
                                    )}

                                    {/* Contact Writer Button - Hidden for own scripts */}
                                    {!isOwnScript && script.author?.display_name && script.author?.nickname && (
                                        <div className="mt-3 text-center">
                                            <button
                                                type="button"
                                                className="btn btn-outline-light btn-sm"
                                                onClick={onContactWriterClick}
                                            >
                                                <i className="fa fa-envelope me-1"></i> Contact Writer
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Column 2: Logline & USP (non-vault only) / USP only (vault) */}
                        <div className="col-7 col-lg-3 mb-3 mb-lg-0">
                            <div className="screenplay_content">
                                {/* Logline — only for non-vault (vault shows it under poster) */}
                                {!script.isVault && (
                                    <fieldset>
                                        <legend>Logline</legend>
                                        <p>{script.logline || 'No logline available'}</p>
                                    </fieldset>
                                )}

                                {/* USP — prefers distinctive_experience from pcrh_usp, falls back to legacy free-form */}
                                {(hasStructuredUsp && pcrhUsp.distinctive_experience) ? (
                                    <fieldset>
                                        <legend>USP</legend>
                                        <p>{pcrhUsp.distinctive_experience}</p>
                                    </fieldset>
                                ) : legacyUsp ? (
                                    <fieldset>
                                        <legend>USP</legend>
                                        <p>{legacyUsp}</p>
                                    </fieldset>
                                ) : null}
                            </div>

                            {/* Author Info — hidden for vault/showcase movies */}
                            {!script.isVault && script.author?.display_name && script.author?.nickname && (
                                <p className="text-white mt-2">
                                    Uploaded by: <a
                                        href={`/portfolios/${script.author.nickname}`}
                                        className="text-white text-decoration-underline"
                                    >
                                        {script.author.display_name}
                                    </a>
                                </p>
                            )}
                        </div>
                        
                        {/* Column 3: Emotion Chart - Uses 6 cols (half width) on mobile */}
                        <div className="col-6 col-lg-3 mb-3 mb-lg-0">
                            <EmotionChart 
                                emotionsData={script.emotions} 
                                emotionSummaries={emotionSummaries}
                                allScripts={allScripts}
                            />
                        </div>
                        
                        {/* Column 4: Radar Chart - Uses 6 cols (half width) on mobile */}
                        <div className="col-6 col-lg-3">
                            {detailLoading && !script.tooltips && (
                                <div className="text-white-50 small mb-1">Loading details…</div>
                            )}
                            <RadarChart script={script} allScripts={allScripts} />
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
}
